import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useSignup } from '../../../../../hooks/useSignup';
import { SignupFormComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import EmptyComponent from '../../../pageBuilder/EmptyComponent';
import SignupForm from '../../../signup/SignupForm';
import SignupHeader from '../../../signup/SignupHeader/index';
import { getAltSignup } from '../../../../../services/services';
import { AltSignup } from '../../../../../interfaces/interfaces';
import { Skeleton } from '@mui/material';

const SignupFormComponent = ({ uiComponent }: UiComponentElementInterface<SignupFormComponentInterface>) => {
  const { t } = useTranslation('pagebuilder');
  const { isUserAlreadySignedUp } = useSignup();

  const { event, user } = useSelector(({ common: { event, user } }) => ({ event, user }), shallowEqual);
  const altSignups = useSelector((state) => state.admin.altSignups);

  const [isAlreadySigned, setIsAlreadySigned] = useState(false);
  const [altSignup, setAltSignup] = useState<AltSignup | null | undefined>();

  async function checkStatus() {
    if (!user) return;
    setIsAlreadySigned((await isUserAlreadySignedUp()) === true);
  }

  async function fetchAltSignup() {
    if (!uiComponent?.altSignupId || altSignup !== undefined || altSignups.length > 0) return;

    try {
      const signupForm = await getAltSignup(uiComponent?.altSignupId);
      setAltSignup(signupForm.data);
    } catch (err) {
      console.error(err);
      setAltSignup(null);
    }
  }

  useEffect(() => {
    fetchAltSignup();
  }, [altSignup]);

  useEffect(() => {
    setAltSignup(altSignups.find((form) => form._id === uiComponent?.altSignupId) ?? null);
  }, [uiComponent?.altSignupId, altSignups]);

  useEffect(() => {
    checkStatus();
  }, [user]);

  if (uiComponent?.altSignupId && altSignup === undefined) return <Skeleton animation="wave" height={100} width="100%" />;

  return (
    <div
      className={`w-full grid place-items-center`}
      style={{
        color: uiComponent?.styling?.textColor || '#000',
      }}>
      {!isAlreadySigned || uiComponent?.showUserRegisteredInfo ? (
        <div className="w-full max-w-[1333px] min-w-[280px]">
          {uiComponent?.showFormHeader && <SignupHeader event={event} uiComponent={uiComponent} />}
          <SignupForm
            event={event}
            isPageBuilder={true}
            customAlreadyRegisteredText={uiComponent?.userRegisteredInfo}
            showBackToEventButton={uiComponent?.showBackToEventButton}
            showMultipleButton={uiComponent?.multipleTickets}
            redirectToSuccessPage={uiComponent?.redirectToSignupSuccess}
            redirectToOrderOverview={uiComponent?.redirectToOrderOverview}
            fieldColors={uiComponent?.styling.textColor}
            fieldsTextColor={uiComponent?.customStyling?.fieldsTextColor}
            fieldsBorderColor={uiComponent?.customStyling?.fieldsBorderColor}
            selectFieldSelectedColor={uiComponent?.customStyling?.selectFieldSelectedColor}
            buttonColor={uiComponent?.customStyling?.buttonColor}
            buttonTextColor={uiComponent?.customStyling?.buttonTextColor}
            buttonWidth={uiComponent?.customStyling?.buttonWidth}
            btnFontSize={uiComponent?.customStyling?.btnFontSize}
            redirectToUrl={uiComponent?.redirectToUrl}
            redirectUrl={uiComponent?.redirectUrl}
            newTabOnSuccess={uiComponent?.newTabOnSuccess}
            signupButtonText={uiComponent?.signupButtonText}
            componentId={uiComponent?._id}
            altSignup={altSignup !== null ? altSignup : undefined}
            showTicketGate={uiComponent?.showTicketGate}
            dataPrivacySentence={uiComponent?.showDefaultDataProtectionNotice ? undefined : uiComponent?.dataPrivacySentence}
            showUserRegisteredInfo={uiComponent?.showUserRegisteredInfo}
            forceNewMail={uiComponent?.forceNewMail}
            showEmailConfirmation={uiComponent?.showEmailConfirmation}
            hideGeneratedEmail={uiComponent?.hideGeneratedEmail}
          />
        </div>
      ) : (
        <EmptyComponent componentName={t('pagebuilder:signup-form')} />
      )}
    </div>
  );
};

export default SignupFormComponent;
